// Facturer une entreprise : les règles, communes au navigateur et au serveur.
//
// Quand une entreprise règle la formation d'un apprenant, la facture doit
// porter sa raison sociale et son adresse, son numéro de TVA
// intracommunautaire au-delà de 150 € HT, et, à partir du 1er septembre 2027
// pour les PME, le SIREN du client français (mentions obligatoires :
// service-public F31808 ; réforme de la facturation électronique). Rien ici ne
// parle à Stripe : lib/stripeCompanyBilling.js s'en charge, côté serveur.

import { getPostalCodeError, normalizePostalCode } from "@/lib/billingAddress";

/**
 * Préfixe du numéro de TVA intracommunautaire selon le pays de l'entreprise.
 * La Grèce écrit EL ; Monaco et les départements d'outre-mer relèvent de
 * numéros français.
 */
// prettier-ignore
const VAT_PREFIXES = {
    AT: "AT", BE: "BE", BG: "BG", CY: "CY", CZ: "CZ", DE: "DE", DK: "DK",
    EE: "EE", ES: "ES", FI: "FI", FR: "FR", GR: "EL", HR: "HR", HU: "HU",
    IE: "IE", IT: "IT", LT: "LT", LU: "LU", LV: "LV", MT: "MT", NL: "NL",
    PL: "PL", PT: "PT", RO: "RO", SE: "SE", SI: "SI", SK: "SK",
    MC: "FR", GP: "FR", MQ: "FR", GF: "FR", RE: "FR", YT: "FR",
};

/** Pays où une entreprise a un SIREN : la France et ses départements d'outre-mer. */
const SIREN_COUNTRIES = new Set(["FR", "GP", "MQ", "GF", "RE", "YT"]);

/** Préfixe TVA attendu pour ce pays, ou null s'il n'a pas de TVA européenne. */
export function vatPrefixFor(country) {
    return VAT_PREFIXES[country] || null;
}

/** Le formulaire demande-t-il le SIREN pour ce pays ? */
export function usesSiren(country) {
    return SIREN_COUNTRIES.has(country);
}

/** « 950 731 331 », « 950731331 » : on ne garde que les chiffres. */
export function normalizeSiren(value) {
    return String(value || "").replace(/\D/g, "");
}

/**
 * Un SIREN compte 9 chiffres et respecte la clé de Luhn. Seule exception
 * connue, La Poste (356 000 000), dont la somme des chiffres est un multiple
 * de 5.
 */
export function isValidSiren(siren) {
    if (!/^\d{9}$/.test(siren)) return false;
    if (siren === "356000000") return true;
    let sum = 0;
    for (let i = 0; i < 9; i++) {
        let digit = Number(siren[i]);
        if (i % 2 === 1) {
            digit *= 2;
            if (digit > 9) digit -= 9;
        }
        sum += digit;
    }
    return sum % 10 === 0;
}

/** Numéro de TVA français déduit d'un SIREN : FR, clé sur deux chiffres, SIREN. */
export function frenchVatFromSiren(siren) {
    const key = (12 + 3 * (Number(siren) % 97)) % 97;
    return `FR${String(key).padStart(2, "0")}${siren}`;
}

/**
 * Remet un numéro de TVA à la forme attendue par Stripe : majuscules, sans
 * espaces ni ponctuation. Saisi sans son préfixe, il reçoit celui du pays.
 */
export function normalizeVat(value, country) {
    const clean = String(value || "")
        .toUpperCase()
        .replace(/[^0-9A-Z]/g, "");
    if (!clean) return "";
    const prefix = vatPrefixFor(country);
    if (prefix && !/^[A-Z]{2}/.test(clean)) return `${prefix}${clean}`;
    return clean;
}

/**
 * Vérifie et met en forme ce que l'utilisateur a saisi pour son entreprise.
 * Le pays et le code postal suivent les règles de lib/billingAddress.js.
 *
 * @returns {{ ok: true, company: object } | { ok: false, errorKey: string, field: string }}
 */
export function validateCompanyBilling(input = {}) {
    const country = String(input.country || "").toUpperCase();
    const name = String(input.name || "").trim().replace(/\s+/g, " ");
    const line1 = String(input.line1 || "").trim();
    const line2 = String(input.line2 || "").trim();
    const city = String(input.city || "").trim();
    const postalCode = normalizePostalCode(country, input.postalCode);

    if (name.length < 2 || name.length > 120) {
        return { ok: false, field: "name", errorKey: "billing_company_error_name" };
    }
    if (!country) {
        return { ok: false, field: "country", errorKey: "address_country_required" };
    }
    if (!line1 || line1.length > 200) {
        return { ok: false, field: "line1", errorKey: "billing_company_error_street" };
    }
    const postalError = getPostalCodeError(country, postalCode);
    if (postalError) {
        return { ok: false, field: "postalCode", errorKey: postalError };
    }
    if (!city || city.length > 120) {
        return { ok: false, field: "city", errorKey: "billing_company_error_city" };
    }

    // Service comptable : la facture y part à chaque paiement. Facultatif,
    // mais mal écrit il n'arriverait nulle part, d'où le contrôle.
    const email = String(input.email || "").trim();
    if (email && (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email) || email.length > 160)) {
        return { ok: false, field: "email", errorKey: "billing_company_error_email" };
    }

    let siren = "";
    if (usesSiren(country)) {
        siren = normalizeSiren(input.siren);
        if (!isValidSiren(siren)) {
            return { ok: false, field: "siren", errorKey: "billing_company_error_siren" };
        }
    }

    let vatNumber = "";
    const prefix = vatPrefixFor(country);
    if (prefix && input.vatNumber) {
        vatNumber = normalizeVat(input.vatNumber, country);
        if (!/^[A-Z]{2}[0-9A-Z]{2,13}$/.test(vatNumber)) {
            return { ok: false, field: "vatNumber", errorKey: "billing_company_error_vat_format" };
        }
        if (vatNumber.slice(0, 2) !== prefix) {
            return { ok: false, field: "vatNumber", errorKey: "billing_company_error_vat_country" };
        }
        // En France, le numéro de TVA se déduit du SIREN : une faute de
        // frappe dans l'un ou l'autre se voit tout de suite.
        if (siren && prefix === "FR" && vatNumber !== frenchVatFromSiren(siren)) {
            return { ok: false, field: "vatNumber", errorKey: "billing_company_error_vat_siren" };
        }
    }

    return {
        ok: true,
        company: {
            name,
            country,
            line1,
            line2,
            postalCode,
            city,
            siren,
            vatNumber,
            email,
        },
    };
}
